import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpIcon from '@mui/icons-material/Help';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StorageIcon from '@mui/icons-material/Storage';
import GoogleIcon from '@mui/icons-material/Google';
import CloudIcon from '@mui/icons-material/Cloud';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BoltIcon from '@mui/icons-material/Bolt';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Menu,
    MenuItem,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Checkbox,
    Chip,
    Snackbar,
    Alert,
    Breadcrumbs,
    Link,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import { clearStoredAuthTokens } from '../utils/auth';
import { environment } from '../../environments/environment';
import {
    GoogleDriveService,
    GoogleDriveFile,
    GoogleDriveAuthState,
    GoogleDriveFolder,
    GoogleDriveBreadcrumb,
} from '../api/googleDriveService';

import Sidebar from './Sidebar';
import { dsVar, ink } from '../theme/tokens';

interface OperatorDetails {
    _id?: string;
    id?: string;
    operator_name?: string;
    operator_description?: string;
    user_instructions?: string;
}

// ─────────────────────────────────────────────
// Helper: localised user / org context
// ─────────────────────────────────────────────
const getOrgId = () => localStorage.getItem('orgId') || '';
const getUserId = () => localStorage.getItem('userId') || '';
const getToken = () => localStorage.getItem('token') || '';

const OperatorLaunch = () => {
    const { operatorId } = useParams<{ operatorId: string }>();
    const navigate = useNavigate();

    // ── Operator state ───────────────────────────────
    const [operator, setOperator] = useState<OperatorDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);

    // ── Snackbar ───────────────────────────────────
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
        open: false,
        message: '',
        severity: 'info',
    });
    const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'info') =>
        setSnackbar({ open: true, message, severity });

    // ── Google Drive state ─────────────────────────
    const [googleDriveStatus, setGoogleDriveStatus] = useState<'idle' | 'authenticating' | 'connected' | 'configured' | 'error'>('idle');
    const [googleDriveAuthState, setGoogleDriveAuthState] = useState<GoogleDriveAuthState>({ isAuthenticated: false, sessionId: '' });
    const [showGoogleDriveDialog, setShowGoogleDriveDialog] = useState(false);
    const [googleDriveFiles, setGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);
    const [isLoadingGoogleFiles, setIsLoadingGoogleFiles] = useState(false);
    const [googleDriveSearchQuery, setGoogleDriveSearchQuery] = useState('');
    const [folderBreadcrumb, setFolderBreadcrumb] = useState<GoogleDriveBreadcrumb[]>([{ id: 'root', name: 'My Drive' }]);
    const [currentFolderId, setCurrentFolderId] = useState<string>('root');
    const [activeTab, setActiveTab] = useState<'myDrive' | 'shared' | 'teamDrives'>('myDrive');
    const [selectedGoogleFiles, setSelectedGoogleFiles] = useState<GoogleDriveFile[]>([]);
    const [integrationId, setIntegrationId] = useState<string>('');
    const [isSavingSelection, setIsSavingSelection] = useState(false);

    // ── GoogleDriveService ref ─────────────────────
    const googleDriveService = useRef<GoogleDriveService | null>(null);

    // ── Auth polling cleanup ─────────────────────────
    const authPollInterval = useRef<ReturnType<typeof setInterval> | null>(null);
    const authPollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ─────────────────────────────────────────────────
    // Initialise GoogleDriveService
    // ─────────────────────────────────────────────────
    useEffect(() => {
        googleDriveService.current = new GoogleDriveService({
            onAuthStateChange: (state: GoogleDriveAuthState) => setGoogleDriveAuthState(state),
            onFilesLoaded: (files: GoogleDriveFile[]) => setGoogleDriveFiles(files),
            onError: (err: string) => showSnackbar(err, 'error'),
            onSuccess: (msg: string) => showSnackbar(msg, 'success'),
            onDownloadProgress: () => { },
            onLoadingStateChange: (isLoading: boolean) => setIsLoadingGoogleFiles(isLoading),
            onFilePreviewLoaded: () => { },
            onFolderBreadcrumbLoaded: (bc: GoogleDriveBreadcrumb[]) => setFolderBreadcrumb(bc),
            onFolderTreeLoaded: () => { },
            onFileInfoLoaded: () => { },
        });

        return () => {
            googleDriveService.current?.destroy();
            if (authPollInterval.current) clearInterval(authPollInterval.current);
            if (authPollTimeout.current) clearTimeout(authPollTimeout.current);
        };
    }, []);

    // ─────────────────────────────────────────────────
    // Fetch operator
    // ─────────────────────────────────────────────────
    useEffect(() => {
        const fetchOperatorDetails = async () => {
            try {
                const response = await axiosInstance.get(`/operators/${operatorId}`);
                if (response.data) {
                    const operatorData = response.data.operator || response.data;
                    setOperator(operatorData);
                }
            } catch (error) {
                console.error('Failed to fetch operator details:', error);
            } finally {
                setLoading(false);
            }
        };
        if (operatorId) fetchOperatorDetails();
    }, [operatorId]);

    // ─────────────────────────────────────────────────
    // Google Drive – persist integration via POST
    // ─────────────────────────────────────────────────
    const persistIntegration = async (sessionId: string): Promise<string> => {
        try {
            const payload = {
                orgId: getOrgId(),
                provider: 'google-drive',
                integrationType: 'cloud-storage',
                displayName: `Google Drive – ${operator?.operator_name || 'Operator'}`,
                status: 'active',
                auth: {
                    authMethod: 'oauth2',
                    // sessionId acts as the token handle; the backend manages raw tokens server-side
                    accessToken: sessionId,
                    refreshToken: '',
                    expiresAt: new Date(Date.now() + 3600 * 1000).toISOString(),
                    scopes: [
                        'https://www.googleapis.com/auth/drive.readonly',
                        'https://www.googleapis.com/auth/drive.file',
                    ],
                },
                config: {
                    apiVersion: 'v3',
                    rootFolderId: 'root',
                },
                metadata: {
                    connectedBy: getUserId(),
                },
            };

            const response = await axios.post(
                `${environment.apiUrl}/manage/integrations`,
                payload,
                { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } }
            );

            const newIntegrationId =
                response.data?.integrationId ||
                response.data?._id ||
                response.data?.id ||
                '';
            return newIntegrationId;
        } catch (err: any) {
            console.error('Failed to persist integration:', err);
            // Non-fatal – we still open the picker even if persistence fails
            return '';
        }
    };

    // ─────────────────────────────────────────────────
    // Google Drive – load files for current folder/tab
    // ─────────────────────────────────────────────────
    const loadGoogleDriveFiles = async (sessionId: string, folderId = 'root', searchQuery = '') => {
        if (!googleDriveService.current) return;
        try {
            await googleDriveService.current.loadFiles(sessionId, searchQuery || undefined, folderId, 1, 100);
        } catch (err: any) {
            if (err?.message === 'jwt_expired') {
                navigate('/login');
            } else if (err?.message === 'Session not authenticated') {
                showSnackbar('Session expired. Please reconnect Google Drive.', 'error');
                setGoogleDriveStatus('error');
            } else {
                showSnackbar(err?.message || 'Failed to load Google Drive files', 'error');
            }
        }
    };

    const loadSharedFiles = async (sessionId: string) => {
        if (!googleDriveService.current) return;
        try {
            await googleDriveService.current.loadSharedFiles(sessionId, 100);
        } catch (err: any) {
            showSnackbar(err?.message || 'Failed to load shared files', 'error');
        }
    };

    const loadTeamDrives = async (sessionId: string) => {
        if (!googleDriveService.current) return;
        try {
            await googleDriveService.current.loadTeamDrives(sessionId, 50);
        } catch (err: any) {
            showSnackbar(err?.message || 'Failed to load team drives', 'error');
        }
    };

    // ─────────────────────────────────────────────────
    // Google Drive – initiate OAuth2 auth
    // ─────────────────────────────────────────────────
    const initiateGoogleDriveAuth = async () => {
        if (!googleDriveService.current) {
            showSnackbar('Google Drive service not ready', 'error');
            return;
        }
        if (googleDriveAuthState.isAuthenticated && googleDriveAuthState.sessionId) {
            // Already authenticated – open picker directly
            setGoogleDriveFiles([]);
            setSelectedGoogleFiles([]);
            setActiveTab('myDrive');
            setFolderBreadcrumb([{ id: 'root', name: 'My Drive' }]);
            setCurrentFolderId('root');
            setShowGoogleDriveDialog(true);
            await loadGoogleDriveFiles(googleDriveAuthState.sessionId, 'root');
            return;
        }

        try {
            setGoogleDriveStatus('authenticating');
            showSnackbar('Opening Google authentication…', 'info');

            const result = await googleDriveService.current.initiateAuth();
            const { sessionId, authUrl } = result;

            const authWindow = googleDriveService.current.openAuthWindow(authUrl);
            if (!authWindow) {
                showSnackbar('Popup blocked – please allow popups for this site.', 'error');
                setGoogleDriveStatus('error');
                return;
            }

            // Poll every 2 seconds – mirrors DocsInsights pattern exactly
            authPollInterval.current = setInterval(async () => {
                try {
                    let isWindowClosed = false;
                    try { isWindowClosed = authWindow.closed; } catch (_) { /* cross-origin */ }

                    let isOnCallbackPage = false;
                    try {
                        isOnCallbackPage = (authWindow.location?.href || '').includes('/analytics/google-drive/auth/callback');
                    } catch (_) { /* cross-origin restriction */ }

                    if (!isWindowClosed && !isOnCallbackPage) return;

                    // Window closed / reached callback – stop polling
                    if (authPollInterval.current) clearInterval(authPollInterval.current);
                    if (authPollTimeout.current) clearTimeout(authPollTimeout.current);

                    try { authWindow.close(); } catch (_) { }

                    // Wait for backend to process tokens
                    showSnackbar('Verifying authentication…', 'info');
                    await new Promise(r => setTimeout(r, 2000));

                    if (!googleDriveService.current) return;

                    let isAuthenticated = await googleDriveService.current.checkAuthStatus(sessionId);

                    if (!isAuthenticated) {
                        // Fallback – try a quick file load to see if backend already has tokens
                        try {
                            await googleDriveService.current.loadFiles(sessionId, undefined, undefined, 1, 5);
                            isAuthenticated = true;
                        } catch (_) {
                            // One more retry after 3s
                            await new Promise(r => setTimeout(r, 3000));
                            isAuthenticated = await googleDriveService.current.checkAuthStatus(sessionId);
                        }
                    }

                    if (isAuthenticated) {
                        setGoogleDriveAuthState({ isAuthenticated: true, sessionId });
                        setGoogleDriveStatus('connected');
                        showSnackbar('✅ Google Drive connected!', 'success');

                        // Persist integration record
                        const newIntegrationId = await persistIntegration(sessionId);
                        if (newIntegrationId) setIntegrationId(newIntegrationId);

                        // Open file picker
                        setGoogleDriveFiles([]);
                        setSelectedGoogleFiles([]);
                        setActiveTab('myDrive');
                        setFolderBreadcrumb([{ id: 'root', name: 'My Drive' }]);
                        setCurrentFolderId('root');
                        setShowGoogleDriveDialog(true);
                        await loadGoogleDriveFiles(sessionId, 'root');
                    } else {
                        setGoogleDriveStatus('error');
                        showSnackbar('Authentication cancelled or failed. Please try again.', 'error');
                    }
                } catch (pollErr) {
                    // Ignore transient poll errors
                }
            }, 2000);

            // 5-minute global timeout
            authPollTimeout.current = setTimeout(() => {
                if (authPollInterval.current) clearInterval(authPollInterval.current);
                try { authWindow.close(); } catch (_) { }
                setGoogleDriveStatus('idle');
                showSnackbar('Authentication timed out. Please try again.', 'error');
            }, 300_000);

        } catch (err: any) {
            setGoogleDriveStatus('error');
            if (err?.message === 'jwt_expired') {
                navigate('/login');
            } else {
                showSnackbar(`Authentication failed: ${err?.message || 'Unknown error'}`, 'error');
            }
        }
    };

    // ─────────────────────────────────────────────────
    // Google Drive – folder navigation
    // ─────────────────────────────────────────────────
    const handleFolderNavigation = async (file: GoogleDriveFile) => {
        if (!googleDriveService.current || !googleDriveAuthState.sessionId) return;
        if (!file.mimeType.includes('folder')) return;
        setCurrentFolderId(file.id);
        // Update breadcrumb
        setFolderBreadcrumb(prev => {
            const existingIdx = prev.findIndex(b => b.id === file.id);
            if (existingIdx !== -1) return prev.slice(0, existingIdx + 1);
            return [...prev, { id: file.id, name: file.name }];
        });
        await loadGoogleDriveFiles(googleDriveAuthState.sessionId, file.id);
    };

    const handleBreadcrumbNavigation = async (breadcrumb: GoogleDriveBreadcrumb) => {
        if (!googleDriveAuthState.sessionId) return;
        setCurrentFolderId(breadcrumb.id);
        setFolderBreadcrumb(prev => {
            const idx = prev.findIndex(b => b.id === breadcrumb.id);
            return idx !== -1 ? prev.slice(0, idx + 1) : prev;
        });
        await loadGoogleDriveFiles(googleDriveAuthState.sessionId, breadcrumb.id);
    };

    // ─────────────────────────────────────────────────
    // Google Drive – tab switch
    // ─────────────────────────────────────────────────
    const handlePickerTabChange = async (_: React.SyntheticEvent, newTab: number) => {
        const tabs: Array<'myDrive' | 'shared' | 'teamDrives'> = ['myDrive', 'shared', 'teamDrives'];
        const tab = tabs[newTab];
        setActiveTab(tab);
        setGoogleDriveFiles([]);
        setFolderBreadcrumb([{ id: 'root', name: tab === 'myDrive' ? 'My Drive' : tab === 'shared' ? 'Shared with Me' : 'Team Drives' }]);
        setCurrentFolderId('root');
        setGoogleDriveSearchQuery('');
        if (!googleDriveAuthState.sessionId) return;

        if (tab === 'myDrive') await loadGoogleDriveFiles(googleDriveAuthState.sessionId, 'root');
        else if (tab === 'shared') await loadSharedFiles(googleDriveAuthState.sessionId);
        else await loadTeamDrives(googleDriveAuthState.sessionId);
    };

    // ─────────────────────────────────────────────────
    // Google Drive – search
    // ─────────────────────────────────────────────────
    const handleSearch = async () => {
        if (!googleDriveAuthState.sessionId) return;
        if (googleDriveSearchQuery.trim()) {
            await loadGoogleDriveFiles(googleDriveAuthState.sessionId, currentFolderId, googleDriveSearchQuery);
        } else {
            await loadGoogleDriveFiles(googleDriveAuthState.sessionId, currentFolderId);
        }
    };

    // ─────────────────────────────────────────────────
    // Google Drive – file selection toggle
    // ─────────────────────────────────────────────────
    const toggleFileSelection = (file: GoogleDriveFile) => {
        setSelectedGoogleFiles(prev => {
            const exists = prev.find(f => f.id === file.id);
            return exists ? prev.filter(f => f.id !== file.id) : [...prev, file];
        });
    };

    // ─────────────────────────────────────────────────
    // Google Drive – persist selection via PUT
    // ─────────────────────────────────────────────────
    const handleConfirmSelection = async () => {
        if (!integrationId) {
            showSnackbar('Integration not yet persisted – please re-connect Google Drive.', 'error');
            return;
        }
        setIsSavingSelection(true);
        try {
            const folders = selectedGoogleFiles.filter(f => f.mimeType.includes('folder'));
            const files = selectedGoogleFiles.filter(f => !f.mimeType.includes('folder'));

            const payload = {
                updatedBy: getUserId(),
                config: {
                    rootFolderId: folders[0]?.id || currentFolderId || 'root',
                    selectedFolders: folders.map(f => ({ id: f.id, name: f.name })),
                    selectedFiles: files.map(f => ({ id: f.id, name: f.name })),
                    fileFilters: {
                        includeMimeTypes: ['application/pdf', 'text/csv'],
                        maxFileSizeMB: 50,
                    },
                    syncFrequency: 'daily',
                    depthLimit: 3,
                },
            };

            await axios.put(
                `${environment.apiUrl}/manage/integrations/${integrationId}`,
                payload,
                { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } }
            );

            setGoogleDriveStatus('configured');
            setShowGoogleDriveDialog(false);
            showSnackbar(`✅ Google Drive configuration saved – ${selectedGoogleFiles.length} item(s) selected`, 'success');
        } catch (err: any) {
            showSnackbar(err?.response?.data?.error || err?.message || 'Failed to save selection', 'error');
        } finally {
            setIsSavingSelection(false);
        }
    };

    // ─────────────────────────────────────────────────
    // Menu handlers
    // ─────────────────────────────────────────────────
    const handleUserMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
        setUserMenuAnchorEl(event.currentTarget);
    const handleUserMenuClose = () => setUserMenuAnchorEl(null);
    const handleSignOut = () => {
        clearStoredAuthTokens();
        handleUserMenuClose();
        navigate('/login');
    };

    // ─────────────────────────────────────────────────
    // Derived helpers
    // ─────────────────────────────────────────────────
    const tabIndex = activeTab === 'myDrive' ? 0 : activeTab === 'shared' ? 1 : 2;
    const filteredFiles = googleDriveSearchQuery.trim()
        ? googleDriveFiles
        : googleDriveFiles;

    const getFileIcon = (mimeType: string) => {
        if (mimeType?.includes('folder')) return <FolderIcon sx={{ color: dsVar('accent-fg'), fontSize: 20 }} />;
        if (mimeType?.includes('spreadsheet') || mimeType?.includes('csv') || mimeType?.includes('excel'))
            return <InsertDriveFileIcon sx={{ color: ink('#34A853'), fontSize: 20 }} />;
        if (mimeType?.includes('document') || mimeType?.includes('pdf'))
            return <InsertDriveFileIcon sx={{ color: ink('#4285F4'), fontSize: 20 }} />;
        return <InsertDriveFileIcon sx={{ color: ink('#8AB4F8'), fontSize: 20 }} />;
    };

    const getDriveButtonLabel = () => {
        if (googleDriveStatus === 'authenticating') return 'Connecting…';
        if (googleDriveStatus === 'configured') return 'Configured';
        if (googleDriveStatus === 'connected') return 'Browse Drive';
        return 'Google Drive';
    };

    // ─────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-page">
                <CircularProgress style={{ color: dsVar('accent-fg') }} />
            </div>
        );
    }

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg">
            <Sidebar activePage="hub" showBackButton={true} />

            {/* ── Main Content ── */}
            <main className="flex-1 flex flex-col overflow-hidden bg-page">
                {/* Header */}
                <header className="h-16 border-b border-line/50 bg-page/80 backdrop-blur-md px-8 flex items-center justify-between sticky top-0 z-10 m-0">
                    <div className="flex-1 max-w-2xl text-left">
                        <div className="relative group flex items-center justify-start h-full">
                            <SearchIcon className="absolute left-3 text-fg-muted group-focus-within:text-accent-fg transition-colors" />
                            <input
                                className="w-full bg-surface border-none rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-1 focus:ring-accent-fg placeholder:text-fg-muted text-fg outline-none"
                                placeholder="Search content, operators, or previous analysis..."
                                type="text"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-4 ml-8">
                        <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
                            <NotificationsIcon />
                        </button>
                        <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
                            <HelpIcon />
                        </button>
                        <button
                            className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
                            onClick={handleUserMenuOpen}
                        >
                            <AccountCircleIcon />
                        </button>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto flex">
                    <div className="flex-1 flex flex-col p-8 space-y-8 max-w-4xl">
                        {/* Operator Header */}
                        <div className="space-y-2 text-left">
                            <h2 className="text-2xl font-bold text-fg">{operator?.operator_name}</h2>
                            <p className="text-fg-2">{operator?.operator_description}</p>
                        </div>

                        {/* User Instructions */}
                        <div className="bg-surface border border-line rounded-2xl p-6 relative min-h-[180px] flex items-stretch">
                            <div className="absolute top-0 left-0 w-1 h-full bg-accent"></div>
                            <div className="flex gap-4 w-full">
                                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center shrink-0">
                                    <span className="text-on-accent font-bold text-xs">AI</span>
                                </div>
                                <div className="space-y-4 text-left w-full">
                                    <h3 className="font-semibold text-accent-fg text-sm uppercase tracking-wider">Data[S]cient Assistant</h3>
                                    <div className="text-fg-body leading-relaxed whitespace-pre-wrap break-words">
                                        {operator?.user_instructions || 'To proceed, please connect your data sources or upload a spreadsheet.'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Upload + Quick Connect */}
                        <div className="flex gap-8">
                            {/* Upload Area */}
                            <div className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-line rounded-2xl p-12 hover:border-accent-fg/30 transition-colors bg-surface/30 group cursor-pointer min-h-[400px]">
                                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <CloudUploadIcon style={{ color: dsVar('accent-fg'), fontSize: 32 }} />
                                </div>
                                <h3 className="text-xl font-medium text-fg mb-2">Drag a spreadsheet here.</h3>
                                <p className="text-fg-muted mb-2">Supported formats: CSV, Excel, JSON</p>
                            </div>

                            {/* Quick Connect Options */}
                            <div className="w-80 flex flex-col space-y-6">
                                <div className="space-y-2 text-left">
                                    <h3 className="text-lg font-semibold text-fg">Quick Connect</h3>
                                    <p className="text-xs text-fg-muted">Connect your existing data sources</p>
                                </div>

                                <div className="space-y-3">
                                    {/* ── Google Drive button ── */}
                                    <Tooltip
                                        title={googleDriveStatus === 'configured' ? 'Click to change selection' : googleDriveStatus === 'connected' ? 'Click to browse files' : 'Connect Google Drive'}
                                        placement="left"
                                    >
                                        <button
                                            onClick={initiateGoogleDriveAuth}
                                            disabled={googleDriveStatus === 'authenticating'}
                                            className="w-full flex items-center gap-4 p-4 rounded-xl bg-surface border transition-all group text-left cursor-pointer disabled:cursor-not-allowed"
                                            style={{
                                                borderColor:
                                                    googleDriveStatus === 'configured' ? '#34A853' :
                                                        googleDriveStatus === 'connected' ? '#4285F4' :
                                                            googleDriveStatus === 'error' ? '#EA4335' :
                                                                undefined,
                                            }}
                                        >
                                            <div className="w-10 h-10 rounded-lg bg-fg/5 flex items-center justify-center shrink-0 relative">
                                                {googleDriveStatus === 'authenticating' ? (
                                                    <CircularProgress size={20} style={{ color: ink('#4285F4') }} />
                                                ) : (
                                                    <GoogleIcon sx={{ color: ink('#4285F4') }} />
                                                )}
                                                {(googleDriveStatus === 'connected' || googleDriveStatus === 'configured') && (
                                                    <CheckCircleIcon
                                                        sx={{ color: ink('#34A853'), fontSize: 14, position: 'absolute', bottom: -2, right: -2 }}
                                                    />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-sm text-fg m-0">{getDriveButtonLabel()}</p>
                                                <p className="text-xs text-fg-muted m-0">
                                                    {googleDriveStatus === 'configured'
                                                        ? `${selectedGoogleFiles.length} item(s) selected`
                                                        : googleDriveStatus === 'connected'
                                                            ? 'Authenticated – browse files'
                                                            : 'Sheets & Documents'}
                                                </p>
                                            </div>
                                            {(googleDriveStatus === 'connected' || googleDriveStatus === 'configured') && (
                                                <Chip
                                                    label={googleDriveStatus === 'configured' ? 'Configured' : 'Connected'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: googleDriveStatus === 'configured' ? 'rgba(52,168,83,0.15)' : 'rgba(66,133,244,0.15)',
                                                        color: googleDriveStatus === 'configured' ? ink('#34A853') : ink('#4285F4'),
                                                        fontSize: '0.65rem',
                                                        height: 20,
                                                    }}
                                                />
                                            )}
                                        </button>
                                    </Tooltip>

                                    {/* Data Warehouse */}
                                    <button className="w-full flex items-center gap-4 p-4 rounded-xl bg-surface border border-line hover:border-accent-fg/50 transition-all group text-left cursor-pointer">
                                        <div className="w-10 h-10 rounded-lg bg-fg/5 flex items-center justify-center shrink-0">
                                            <StorageIcon sx={{ color: dsVar('accent-fg') }} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium text-sm text-fg m-0">Data Warehouse</p>
                                            <p className="text-xs text-fg-muted m-0">BigQuery, Snowflake</p>
                                        </div>
                                    </button>

                                    {/* Cloud Storage */}
                                    <button className="w-full flex items-center gap-4 p-4 rounded-xl bg-surface border border-line hover:border-accent-fg/50 transition-all group text-left cursor-pointer">
                                        <div className="w-10 h-10 rounded-lg bg-fg/5 flex items-center justify-center shrink-0">
                                            <CloudIcon sx={{ color: ink('#34A853') }} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium text-sm text-fg m-0">Cloud Storage</p>
                                            <p className="text-xs text-fg-muted m-0">AWS S3, GCP Buckets</p>
                                        </div>
                                    </button>
                                </div>

                                <div className="p-4 rounded-xl bg-accent/5 border border-accent-fg/10 text-left">
                                    <p className="text-xs text-accent-fg leading-relaxed">
                                        <strong>Pro Tip:</strong> Linking multiple sources (e.g. CRM + Database) increases analysis certainty.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-center gap-8 pt-8 border-t border-line/50">
                            <div className="flex items-center gap-2 text-xs text-fg-muted">
                                <VerifiedUserIcon sx={{ fontSize: 14 }} />
                                <span>ENTERPRISE GRADE SECURITY</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-fg-muted">
                                <BoltIcon sx={{ fontSize: 14 }} />
                                <span>REAL-TIME PROCESSING</span>
                            </div>
                        </div>
                    </div>
                </div>

                <Menu
                    anchorEl={userMenuAnchorEl}
                    open={Boolean(userMenuAnchorEl)}
                    onClose={handleUserMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            backgroundColor: dsVar('surface'),
                            color: dsVar('fg-body'),
                            border: `1px solid ${dsVar('line')}`,
                            minWidth: '180px',
                            mt: 1,
                        },
                    }}
                >
                    <MenuItem onClick={handleSignOut} sx={{ color: dsVar('accent-fg') }}>
                        <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                        Sign out
                    </MenuItem>
                </Menu>
            </main>

            {/* ══════════════════════════════════════════════════
                Google Drive File Picker Dialog
            ══════════════════════════════════════════════════ */}
            <Dialog
                open={showGoogleDriveDialog}
                onClose={() => setShowGoogleDriveDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: dsVar('page'),
                        border: `1px solid ${dsVar('line')}`,
                        borderRadius: '16px',
                        color: dsVar('fg-body'),
                        maxHeight: '80vh',
                    },
                }}
            >
                {/* Dialog Header */}
                <DialogTitle sx={{ borderBottom: `1px solid ${dsVar('line')}`, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <GoogleIcon sx={{ color: ink('#4285F4') }} />
                        <Typography variant="h6" sx={{ color: dsVar('fg-body'), fontWeight: 600 }}>
                            Browse Google Drive
                        </Typography>
                        {selectedGoogleFiles.length > 0 && (
                            <Chip
                                label={`${selectedGoogleFiles.length} selected`}
                                size="small"
                                sx={{ ml: 'auto', backgroundColor: 'rgba(66,133,244,0.2)', color: ink('#8AB4F8') }}
                            />
                        )}
                    </Box>
                </DialogTitle>

                {/* Tabs */}
                <Box sx={{ borderBottom: `1px solid ${dsVar('line')}` }}>
                    <Tabs
                        value={tabIndex}
                        onChange={handlePickerTabChange}
                        sx={{
                            px: 2,
                            '& .MuiTab-root': { color: dsVar('fg-2'), textTransform: 'none', minHeight: 44 },
                            '& .Mui-selected': { color: dsVar('accent-fg') },
                            '& .MuiTabs-indicator': { backgroundColor: dsVar('accent') },
                        }}
                    >
                        <Tab label="My Drive" />
                        <Tab label="Shared with Me" />
                        <Tab label="Team Drives" />
                    </Tabs>
                </Box>

                <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* Search bar */}
                    <Box sx={{ p: 2, borderBottom: `1px solid ${dsVar('line')}`, display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search files and folders…"
                            value={googleDriveSearchQuery}
                            onChange={e => setGoogleDriveSearchQuery(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: dsVar('surface'),
                                    color: dsVar('fg-body'),
                                    borderRadius: '8px',
                                    '& fieldset': { borderColor: dsVar('line') },
                                    '&:hover fieldset': { borderColor: '#4285F4' },
                                },
                                '& input': { color: dsVar('fg-body') },
                                '& input::placeholder': { color: dsVar('fg-muted') },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: dsVar('fg-muted'), fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton
                            onClick={handleSearch}
                            sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '8px', color: ink('#4285F4'), '&:hover': { backgroundColor: 'rgba(66,133,244,0.1)' } }}
                        >
                            <SearchIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            onClick={() => loadGoogleDriveFiles(googleDriveAuthState.sessionId, currentFolderId)}
                            sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '8px', color: dsVar('fg-2'), '&:hover': { backgroundColor: dsVar('fg-2', 0.1) } }}
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Breadcrumb */}
                    {activeTab === 'myDrive' && folderBreadcrumb.length > 0 && (
                        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${dsVar('line')}` }}>
                            <Breadcrumbs
                                separator={<NavigateNextIcon sx={{ color: dsVar('fg-faint'), fontSize: 14 }} />}
                                sx={{ '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap', overflowX: 'auto' } }}
                            >
                                {folderBreadcrumb.map((bc, idx) => (
                                    <Link
                                        key={bc.id}
                                        component="button"
                                        onClick={() => handleBreadcrumbNavigation(bc)}
                                        underline="hover"
                                        sx={{
                                            color: idx === folderBreadcrumb.length - 1 ? dsVar('accent-fg') : dsVar('fg-2'),
                                            fontSize: '0.75rem',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            background: 'none',
                                            border: 'none',
                                        }}
                                    >
                                        {bc.name}
                                    </Link>
                                ))}
                            </Breadcrumbs>
                        </Box>
                    )}

                    {/* Loading bar */}
                    {isLoadingGoogleFiles && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#4285F4' }, backgroundColor: dsVar('raised') }} />}

                    {/* File list */}
                    <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1 }}>
                        {!isLoadingGoogleFiles && filteredFiles.length === 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: dsVar('fg-faint') }}>
                                <Typography variant="body2">No files found</Typography>
                            </Box>
                        )}
                        {filteredFiles.map(file => {
                            const isFolder = file.mimeType.includes('folder');
                            const isSelected = selectedGoogleFiles.some(f => f.id === file.id);
                            return (
                                <Box
                                    key={file.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: '8px',
                                        cursor: isFolder ? 'pointer' : 'default',
                                        backgroundColor: isSelected ? 'rgba(66,133,244,0.12)' : 'transparent',
                                        '&:hover': { backgroundColor: isSelected ? 'rgba(66,133,244,0.18)' : dsVar('fg', 0.04) },
                                        transition: 'background-color 0.15s',
                                    }}
                                    onDoubleClick={() => isFolder && handleFolderNavigation(file)}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={() => toggleFileSelection(file)}
                                        onClick={e => e.stopPropagation()}
                                        size="small"
                                        sx={{
                                            color: dsVar('fg-faint'),
                                            '&.Mui-checked': { color: ink('#4285F4') },
                                            p: 0.5,
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, flexShrink: 0 }}>
                                        {getFileIcon(file.mimeType)}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: dsVar('fg-body'),
                                                fontWeight: isFolder ? 500 : 400,
                                                fontSize: '0.8rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {file.name}
                                        </Typography>
                                        {file.modifiedTime && (
                                            <Typography variant="caption" sx={{ color: dsVar('fg-muted'), fontSize: '0.68rem' }}>
                                                Modified {new Date(file.modifiedTime).toLocaleDateString()}
                                                {file.size ? ` · ${(parseInt(file.size) / 1024 / 1024).toFixed(1)} MB` : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                    {isFolder && (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleFolderNavigation(file)}
                                            sx={{ color: dsVar('fg-muted'), '&:hover': { color: dsVar('accent-fg') }, p: 0.5 }}
                                        >
                                            <NavigateNextIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </DialogContent>

                {/* Dialog Actions */}
                <DialogActions sx={{ borderTop: `1px solid ${dsVar('line')}`, px: 3, py: 2, gap: 1 }}>
                    <Typography variant="caption" sx={{ color: dsVar('fg-muted'), flex: 1 }}>
                        {selectedGoogleFiles.length > 0
                            ? `${selectedGoogleFiles.length} item(s) selected — double-click a folder to navigate into it`
                            : 'Select folders or files, then click Save Selection'}
                    </Typography>
                    <Button
                        onClick={() => setShowGoogleDriveDialog(false)}
                        sx={{ color: dsVar('fg-2'), textTransform: 'none', '&:hover': { backgroundColor: dsVar('fg-2', 0.1) } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmSelection}
                        disabled={selectedGoogleFiles.length === 0 || isSavingSelection}
                        startIcon={isSavingSelection ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                        sx={{
                            backgroundColor: '#4285F4',
                            color: '#fff',
                            textTransform: 'none',
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: '#3367D6' },
                            '&.Mui-disabled': { backgroundColor: dsVar('raised'), color: dsVar('fg-faint') },
                        }}
                    >
                        {isSavingSelection ? 'Saving…' : 'Save Selection'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default OperatorLaunch;
